import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ApiError, api } from '../api/client'
import type { SellItem, SellResult } from '../api/types'
import { useAuth } from '../auth'
import { useFusionConfig } from '../hooks/useFusionConfig'
import { useOperation } from '../hooks/useOperation'
import { copyText } from '../lib/clipboard'
import { foldOrderLines, parseOrderLine } from '../lib/finish'
import { generateOutputs } from '../lib/fusion'

type FinishPrepared = {
  text: string
  items: SellItem[]
  errors: string[]
  asked: number
  count: number
}

const PLACEHOLDER = '数字-5-io\n山竹  棋牌   2\n数字-5-uk  舒心棋牌1'

export function Finish() {
  const { user } = useAuth()
  const sellOp = useOperation<SellResult>('finish-sell')
  const fusion = useFusionConfig()
  const managers = fusion.state.managers
  const cabinet = { cabinetNumber: fusion.state.cabinetNumber, cabinetArea: fusion.state.cabinetArea }
  const [orders, setOrders] = useState('')
  const [note, setNote] = useState('')
  const [summary, setSummary] = useState('')
  const [output, setOutput] = useState('')
  const [items, setItems] = useState<SellItem[]>([])
  const [outbound, setOutbound] = useState('')
  const [busy, setBusy] = useState(false)

  async function run() {
    const lines = foldOrderLines(orders)
    if (!lines.length) {
      setNote('先写下单。例如第一行 数字-5-io，下一行 山竹 棋牌 2')
      setSummary('')
      setOutput('')
      return
    }
    if (!fusion.ready) {
      setNote(fusion.error ? '配置没有读到。稍等一下再点，或者先打开配置页看名单在不在。' : '正在读取管理员名单…')
      setSummary('')
      setOutput('')
      return
    }
    if (!managers.length) {
      setNote('还没有管理员。到配置页保存名单后再来。')
      setSummary('')
      setOutput('')
      return
    }
    const problems: string[] = []
    lines.forEach((line, index) => {
      const got = parseOrderLine(line, managers)
      if (!got.ok) problems.push(`第 ${index + 1} 行：${got.error}`)
    })
    if (problems.length) {
      setNote(problems.join('\n'))
      setSummary('')
      setOutput('')
      return
    }

    setBusy(true)
    setOutbound('')
    setItems([])
    sellOp.newIntent()
    setNote('正在从 A 区和 S 区配组…')
    try {
      const data = await api.post<FinishPrepared>('/api/finish/prepare', { text: orders })
      const picked = data.items ?? []
      setItems(picked)
      setOutput(data.text ?? '')
      const text = [
        data.asked === data.count
          ? `已生成 ${data.count} 组。这批已占住，手机和网站不会再配到它们。库存没有出库。`
          : `要 ${data.asked} 组，只配到 ${data.count} 组。已占住的不会再被另一边配走。库存没有出库。`,
        ...(data.errors ?? []),
      ].join('\n')
      setSummary(text)
      setNote(text)
    } catch (e) {
      setNote(e instanceof Error ? e.message : '查询库存失败')
      setSummary('')
      setOutput('')
    } finally {
      setBusy(false)
    }
  }

  async function copyAll() {
    if (!output) return
    const ok = await copyText(output)
    setNote(ok ? '已复制到剪贴板。' : '浏览器拒绝了剪贴板，请手动选择文本。')
  }

  async function copyAndSell() {
    if (!output || !items.length || outbound) return
    const ok = await copyText(output)
    if (!ok) {
      setNote('浏览器拒绝了剪贴板，这批还没有出库。')
      return
    }
    let failed: unknown = null
    const sold = await sellOp.run(async (key) => {
      try {
        return await api.post<SellResult>('/api/domains/sell', { items, remark: '一键完成' }, key)
      } catch (e) {
        failed = e
        throw e
      }
    })
    if (!sold) {
      setNote('文字已复制。出库没有完成，可以再点一次「复制&出库」。\n' + explain(failed))
      return
    }
    setOutbound(sold.batchCode)
    setNote(`已复制，并出库 ${sold.count} 条。批次 ${sold.batchCode}。`)
  }

  const cdn = generateOutputs(
    items.map((item) => item.domain),
    cabinet,
  )
  const cdnReady = cdn.code === 'OK' && !!output

  async function copyCdn(text: string, label: string) {
    if (!text) return
    const ok = await copyText(text)
    setNote(ok ? `已复制${label}。` : '浏览器拒绝了剪贴板，请手动选择文本。')
  }

  return (
    <section className="finish-page">
      <header className="page-head">
        <div>
          <h1>一键完成</h1>
          <p className="muted">1. 取 A 区里指定条件的在库域名，2. 取 S 区任意一条在库域名。不改库存，也不动其他板块。</p>
        </div>
      </header>
      {fusion.error && <p className="notice error">{fusion.error}</p>}
      <div className="finish-layout">
        <section className="card">
          <label className="field">
            <span>下单</span>
            <textarea
              value={orders}
              spellCheck={false}
              rows={12}
              placeholder={PLACEHOLDER}
              onChange={(e) => setOrders(e.target.value)}
            />
          </label>
          <p className="muted">
            格式是「类型-长度-后缀」，空格，再写管理员和组数。组数 1 就是一组：1. 为 A 区符合条件的一条，2. 为 S 区任意一条。名字可以分开写。管理员来自
            <Link to="/fusion-config">配置</Link>。
          </p>
          <button type="button" className="primary" disabled={busy} onClick={() => void run()}>
            一键完成
          </button>
          {note && <p className="finish-note">{note}</p>}
        </section>
        <section className="card">
          <div className="row spread">
            <h2>完成</h2>
            <div className="row">
              <button type="button" onClick={() => void copyAll()} disabled={!output}>
                复制
              </button>
              <button
                type="button"
                className="primary"
                onClick={() => void copyAndSell()}
                disabled={!output || !items.length || busy || sellOp.busy || !user?.canWrite || !!outbound}
                title={user?.canWrite ? '复制完成文字，并把这批域名记为出库' : '当前账号不能出库'}
              >
                复制&出库
              </button>
            </div>
          </div>
          {summary && <p className="finish-note">{summary}</p>}
          <pre className="result-pre">{output || '等待生成'}</pre>
        </section>
      </div>
      <div className="finish-cdn">
        <CdnCard title="带头" text={cdnReady ? cdn.prefix : ''} onCopy={() => void copyCdn(cdn.prefix, '带头')} />
        <CdnCard title="兼容" text={cdnReady ? cdn.compatible : ''} onCopy={() => void copyCdn(cdn.compatible, '兼容')} />
      </div>
      {output && cdn.code === 'E_CABINET_MISSING' && (
        <p className="muted">
          橱柜编号或区域还没填，带头和兼容无法生成。请先到<Link to="/fusion-config">配置</Link>保存。
        </p>
      )}
    </section>
  )
}

function CdnCard({ title, text, onCopy }: { title: string; text: string; onCopy: () => void }) {
  return (
    <section className="card">
      <div className="row spread">
        <h2>{title}</h2>
        <button type="button" onClick={onCopy} disabled={!text}>
          复制
        </button>
      </div>
      <pre className="result-pre">{text || '等待生成'}</pre>
    </section>
  )
}

function explain(e: unknown): string {
  if (e instanceof ApiError) {
    const lines = e.details.map((d) => [d.domain, d.reason].filter(Boolean).join(' ')).filter(Boolean)
    return [e.message, ...lines].join('\n')
  }
  return e instanceof Error ? e.message : '出库失败'
}
